// TopNav.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaUserCircle,
  FaWhatsapp,
  FaPlus,
  FaArrowDown,
  FaArrowUp,
  FaEnvelope,
  FaUserCog,
  FaChevronDown,
  FaStar
} from 'react-icons/fa';
import { useAuthContext } from '../../../context/AuthContext';
import smsApi from '../../../api/messaging/smsApi';
import authApi from '../../../api/auth/authApi';
import { STORAGE_KEYS } from '../../../api/config';
import './style/TopNav.css';
import CreateEventModal from '../../../modals/events/CreateEventModal';

const TopNav = ({ toggleSidebar, sidebarCollapsed }) => {
  const navigate = useNavigate();
  const { events, currentEvent, switchEvent, setDefaultEvent, logout, user } = useAuthContext();
  const [notificationCount, setNotificationCount] = useState(0);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [smsCredits, setSmsCredits] = useState(0);
  const [whatsappCredits, setWhatsappCredits] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [eventSearch, setEventSearch] = useState('');

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
      } else {
        await authApi.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.UID);
      localStorage.removeItem(STORAGE_KEYS.EVENT_ID);
      localStorage.removeItem('selectedEventId');
      localStorage.removeItem('defaultEventId');
      localStorage.removeItem('defaultEvent');
      localStorage.removeItem('selectedEvent');
      setShowLogoutModal(false);
      navigate('/login');
    }
  };

  const getCredits = useCallback(async () => {
    if (!currentEvent) return;
    try {
      const res = await smsApi.getEventCredits();
      let data = res?.data;
      if (Array.isArray(data)) {
        data = data.length > 0 ? data[0] : {};
      }
      if (data && typeof data === 'object') {
        const smsBal = data.sms?.balance !== undefined ? data.sms.balance : (data.sms_balance ?? data.sms_credits ?? 0);
        const waBal = data.whatsapp?.balance !== undefined ? data.whatsapp.balance : (data.whatsapp_balance ?? data.whatsapp_credits ?? 0);
        setSmsCredits(smsBal);
        setWhatsappCredits(waBal);
      }
    } catch (err) {
      console.error("Error loading credits:", err);
      setSmsCredits(0);
      setWhatsappCredits(0);
    }
  }, [currentEvent]);

  useEffect(() => {
    getCredits();
  }, [currentEvent, getCredits]);

  const orderedEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    const sorted = [...events].sort((a, b) => {
      const aDate = new Date(a.event_date || 0).getTime();
      const bDate = new Date(b.event_date || 0).getTime();
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });
    if (!currentEvent) return sorted;
    const currentIndex = sorted.findIndex(event => event.id === currentEvent.id);
    if (currentIndex === -1) return [currentEvent, ...sorted];
    const ordered = [...sorted];
    ordered.splice(currentIndex, 1);
    return [currentEvent, ...ordered];
  }, [events, currentEvent, sortOrder]);

  const effectiveDefaultId = useMemo(() => {
    const savedId = localStorage.getItem('defaultEventId') || user?.default_event_id;
    if (savedId) return String(savedId);
    if (Array.isArray(events)) {
      const foundDefault = events.find(e => e.is_default === 1 || e.is_default === true);
      if (foundDefault) return String(foundDefault.id);
    }
    return null;
  }, [events, user?.default_event_id]);

  const filteredEvents = useMemo(() => {
    if (!eventSearch.trim()) return orderedEvents;
    const term = eventSearch.toLowerCase().trim();
    return orderedEvents.filter(evt => {
      const title = (evt.event_title || evt.name || '').toLowerCase();
      return title.includes(term);
    });
  }, [orderedEvents, eventSearch]);

  return (
    <nav
      className={`top-nav ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
      style={{
        left: windowWidth <= 768
          ? 'max(0px, calc((100vw - 1600px) / 2))'
          : `max(${sidebarCollapsed ? '70px' : '140px'}, calc((100vw - 1600px) / 2 + ${sidebarCollapsed ? '70px' : '140px'}))`,
        right: 'max(0px, calc((100vw - 1600px) / 2))'
      }}
    >
      <div className="left-section">
        <button
          className="pill-btn toggle-btn me-2"
          onClick={toggleSidebar}
          aria-label="Toggle Sidebar"
        >
          <FaBars />
        </button>

        {events && events.length > 0 && currentEvent ? (
          <div className="event-wrapper">
            <Dropdown className="event-dropdown">
              <Dropdown.Toggle
                variant="light"
                id="event-dropdown"
                className="d-flex align-items-center w-100 event-dropdown-toggle"
              >
                <FaUserCircle className="event-icon me-2" />
                <span className="event-title text-truncate">
                  {windowWidth <= 768
                    ? (() => {
                      const title = currentEvent.event_title || currentEvent.name || 'Event';
                      return title.length > 6 ? `${title.slice(0, 6)}..` : title;
                    })()
                    : (currentEvent.event_title || currentEvent.name || 'Event')
                  }
                </span>
                <FaChevronDown className="dropdown-arrow ms-2" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="shadow border-0 pill-dropdown-menu">
                <div className="dropdown-header d-flex justify-content-between align-items-center">
                  <Button
                    variant="link"
                    className="text-decoration-none d-flex align-items-center p-0 m-0 fw-bold text-nowrap create-event-btn"
                    onClick={() => setShowCreateModal(true)}
                    title="Tengeneza Shughuli Mpya"
                  >
                    <div className="plus-icon-wrapper">
                      <FaPlus />
                    </div>
                    Shughuli Mpya
                  </Button>

                  <div className="sort-buttons-wrapper">
                    <button
                      className={`sort-btn ${sortOrder === 'asc' ? 'active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSortOrder('asc'); }}
                      title="Zamani kwanza"
                    >
                      <FaArrowDown />
                    </button>
                    <button
                      className={`sort-btn ${sortOrder === 'desc' ? 'active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSortOrder('desc'); }}
                      title="Mpya kwanza"
                    >
                      <FaArrowUp />
                    </button>
                  </div>
                </div>

                {orderedEvents.length > 5 && (
                  <div className="px-2 py-1 mb-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Tafuta shughuli..."
                      value={eventSearch}
                      onChange={(e) => setEventSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        borderRadius: '20px',
                        fontSize: '0.82rem',
                        padding: '5px 12px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#f8fafc'
                      }}
                    />
                  </div>
                )}

                <div className="events-dropdown-list">
                  {filteredEvents.length > 0 ? (
                    filteredEvents.map(event => {
                      const isDefault = Boolean(effectiveDefaultId && String(event.id) === String(effectiveDefaultId));

                      return (
                        <Dropdown.Item
                          key={event.id || event.event_address}
                          className="d-flex align-items-center justify-content-between pill-dropdown-item"
                          active={currentEvent?.id === event.id}
                          onClick={() => {
                            setEventSearch('');
                            switchEvent(event);
                          }}
                        >
                          <span className="text-truncate flex-grow-1">
                            {event.event_title || event.name}
                          </span>
                          {isDefault && (
                            <span className="badge bg-warning text-dark ms-2 d-inline-flex align-items-center gap-1" style={{ fontSize: '0.68rem', padding: '3px 8px', borderRadius: '12px', fontWeight: '700' }} title="Default event">
                              <FaStar style={{ color: '#d97706' }} size={10} /> 
                            </span>
                          )}
                        </Dropdown.Item>
                      );
                    })
                  ) : (
                    <Dropdown.Item disabled className="pill-dropdown-item text-muted">
                      {eventSearch ? 'Hakuna shughuli inayofanana' : 'Hakuna matukio'}
                    </Dropdown.Item>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        ) : (
          <div className="mt-2 text-muted">Hakuna matukio</div>
        )}
      </div>

      <div className="center-section">
        <div className="credits-container">
          <Link to="/sms-topup" state={{ defaultTab: 'sms' }} className="text-decoration-none credit-link">
            <div className="credit-pill sms-pill">
              <FaEnvelope className="pill-icon" />
              <span className="pill-label">SMS</span>
              <span className="pill-count">{smsCredits}</span>
            </div>
          </Link>

          <Link to="/sms-topup" state={{ defaultTab: 'whatsapp' }} className="text-decoration-none credit-link">
            <div className="credit-pill wa-pill">
              <FaWhatsapp className="pill-icon" />
              <span className="pill-label">WA</span>
              <span className="pill-count">{whatsappCredits}</span>
            </div>
          </Link>
        </div>
      </div>

      <div className="right-section">
        <Dropdown align="end" className="admin-dropdown">
          <Dropdown.Toggle
            variant="transparent"
            id="dropdown-basic"
            className="admin-toggle p-0"
          >
            <div className="admin-pill">
              <FaUserCog className="admin-icon" />
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu className="admin-menu pill-dropdown-menu">
            <Dropdown.Item href="/Account-Setting" className="pill-dropdown-item">
              <FaUser className="menu-icon" /> Profile
            </Dropdown.Item>

            <Dropdown.Item href="/settings" className="pill-dropdown-item">
              <FaCog className="menu-icon" /> Settings
            </Dropdown.Item>
            <Dropdown.Divider />

            <Dropdown.Item
              onClick={handleLogout}
              className="pill-dropdown-item logout-item"
            >
              <FaSignOutAlt className="menu-icon" /> Logout
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <CreateEventModal show={showCreateModal} handleClose={() => setShowCreateModal(false)} />
      </div>
    </nav>
  );
};

export default TopNav;